import { useMemo, useState } from "react";
import { Grid, Box, Typography, Checkbox, Tooltip, IconButton } from "@mui/material";
import TableComponentV2 from "../../../../common/components/TableComponentV2";
import { useZeroSalesData } from "../hooks";
import LoadingComponent from "../../../../common/components/LoadingComponent";
import moment from "moment";
import Item from "../../../../common/components/ItemCard";
import StorefrontTwoToneIcon from '@mui/icons-material/StorefrontTwoTone';
import Inventory2TwoToneIcon from '@mui/icons-material/Inventory2TwoTone';
import InventoryTwoToneIcon from '@mui/icons-material/InventoryTwoTone';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export default function StoreArticleZeroSalesTable({ filters, overviewTableIsEmpty }) {
    const { zeroSalesData, isLoadingZeroSalesData } = useZeroSalesData({
        params: { ...filters },
    });

    const hasLastReceivedDate = useMemo(() => {
        if (!zeroSalesData || zeroSalesData?.length === 0) return false;

        return zeroSalesData[0].hasOwnProperty("last_received_date");
    }, [zeroSalesData])

    const [checkedActions, setCheckedActions] = useState({
        storeDisplay: true,
        orderSettings: true,
        stockProblem: true,
        rentableValue: true,
        noCallOuts: true
    });

    const handleCheckboxChange = (action) => (event) => {
        setCheckedActions((prev) => ({
            ...prev,
            [action]: event.target.checked,
        }));
    };

    const filteredZeroSalesData = useMemo(() => {
        if (!zeroSalesData) return [];
        if (
            !checkedActions.storeDisplay &&
            !checkedActions.orderSettings &&
            !checkedActions.stockProblem &&
            !checkedActions.rentableValue &&
            !checkedActions.noCallOuts
        ) {
            return zeroSalesData;
        }
        return zeroSalesData.filter((row) => {
            const {
                inventory_value: inventoryValue,
                rolling_week_sales_at_cost: rollingSalesAtCost,
                csl: cslValue,
                rentables_articles: rentable,
                display_type_value: displayTypeValue,
                sales_at_cost_transaction_count: salesAtCostCount,
            } = row;

            let resultDaysSupply = null;
            if (rollingSalesAtCost && rollingSalesAtCost !== 0 && rollingSalesAtCost !== '0') {
                const adjustedInventory = inventoryValue - (inventoryValue * (displayTypeValue / 100));
                const finalizedRollingSalesAtCost = (rollingSalesAtCost / salesAtCostCount);
                resultDaysSupply = adjustedInventory / finalizedRollingSalesAtCost;
            }

            const isRentable = rentable === true;
            const isOrderSettings = !isRentable && cslValue >= 75 && resultDaysSupply <= 15;
            const isStoreDisplay = !isRentable && (cslValue >= 75 || resultDaysSupply >= 45) && !isOrderSettings;
            const isStockProblem = !isRentable && cslValue <= 70 && resultDaysSupply <= 15;

            if (checkedActions.rentableValue && isRentable) return true;
            if (checkedActions.orderSettings && isOrderSettings) return true;
            if (
                checkedActions.storeDisplay &&
                isStoreDisplay &&
                (!checkedActions.orderSettings ? !isOrderSettings : true)
            ) return true;
            if (checkedActions.stockProblem && isStockProblem) return true;

            // Only include for No Call-outs if it doesn't match any other action
            const matchesOtherActions =
                isRentable ||
                isOrderSettings ||
                isStoreDisplay ||
                isStockProblem;

            if (checkedActions.noCallOuts && !matchesOtherActions) return true;

            return false;
        });
    }, [zeroSalesData, checkedActions]);

    const actionCounts = useMemo(() => {
        if (!zeroSalesData) return { storeDisplay: 0, orderSettings: 0, stockProblem: 0, rentableValue: 0, noCallOuts: 0 };
        let storeDisplay = 0, orderSettings = 0, stockProblem = 0, rentableValue = 0, noCallOuts = 0;
        zeroSalesData.forEach(row => {
            const {
                inventory_value: inventoryValue,
                rolling_week_sales_at_cost: rollingSalesAtCost,
                csl: cslValue,
                rentables_articles: rentable,
                display_type_value: displayTypeValue,
                sales_at_cost_transaction_count: salesAtCostCount,
            } = row;

            let resultDaysSupply = null;
            if (rollingSalesAtCost && rollingSalesAtCost !== 0 && rollingSalesAtCost !== '0') {
                const adjustedInventory = inventoryValue - (inventoryValue * (displayTypeValue / 100));
                const finalizedRollingSalesAtCost = (rollingSalesAtCost / salesAtCostCount);
                resultDaysSupply = adjustedInventory / finalizedRollingSalesAtCost;
            }

            const isRentable = rentable === true;
            const isOrderSettings = !isRentable && cslValue >= 75 && resultDaysSupply <= 15;
            const isStoreDisplay = !isRentable && (cslValue >= 75 || resultDaysSupply >= 45) && !isOrderSettings;
            const isStockProblem = !isRentable && cslValue <= 70 && resultDaysSupply <= 15;

            if (isStoreDisplay) storeDisplay++;
            if (isOrderSettings) orderSettings++;
            if (isStockProblem) stockProblem++;
            if (isRentable) rentableValue++;
            if (!isRentable && !isOrderSettings && !isStoreDisplay && !isStockProblem) noCallOuts++;
        });

        return { storeDisplay, orderSettings, stockProblem, rentableValue, noCallOuts };
    }, [zeroSalesData]);

    const calcDaysSupply = (row) => {
        const inventoryValue = row.inventory_value ?? row?.row?.original?.inventory_value;
        const salesAtCostCount = row.sales_at_cost_transaction_count
        const rollingSalesAtCost = row.rolling_week_sales_at_cost
        const displayTypeValue = row.display_type_value

        if (rollingSalesAtCost && rollingSalesAtCost !== 0 && rollingSalesAtCost !== '0') {
            const adjustedInventory = inventoryValue - (inventoryValue * (displayTypeValue / 100));
            const finalizedRollingSalesAtCost = (rollingSalesAtCost / salesAtCostCount);
            return adjustedInventory / finalizedRollingSalesAtCost;
        }
        return null;
    };

    const storeZeroSalesColumns = useMemo(
        () => {
            const columns = [
                {
                    id: "article_number",
                    header: "Article Number",
                    accessorKey: "article_number",
                    cell: (row) => (
                        <Box sx={{ width: { sm: '70%' }, marginX: 'auto', my: 1 }}>
                            <Typography>{row?.getValue()}</Typography>
                        </Box>
                    ),
                    customStyle: {
                        width: "10rem",
                        textAlign: "left",
                        verticalAlign: "middle",
                    },
                },
                {
                    id: "article_description",
                    header: "Article Description",
                    accessorKey: "article_description",
                    cell: (row) => (
                        <Box sx={{ marginX: 'auto' }}>
                            <Typography>{row?.getValue()}</Typography>
                        </Box>
                    ),
                    customStyle: { width: "30rem", verticalAlign: "middle" },
                },
                {
                    id: "product_category",
                    header: "Category",
                    accessorKey: "article_category",
                    cell: (row) => (
                        <Box sx={{ width: { sm: '60%' }, marginX: 'auto' }}>
                            <Typography>{row?.getValue()}</Typography>
                        </Box>
                    ),
                    customStyle: {
                        width: "8rem",
                        textAlign: "left",
                        verticalAlign: "middle",
                    },
                },
                {
                    id: "must_have_type",
                    header: "Top",
                    accessorKey: "article_sub_category",
                    cell: (row) => (
                        <Box sx={{ width: { sm: '60%' }, marginX: 'auto', display: "flex", gap: .5 }}>
                            <Typography>Top </Typography>
                            <Typography>{row?.getValue()}</Typography>
                        </Box>
                    ),
                    customStyle: {
                        width: "8rem",
                        textAlign: "left",
                        verticalAlign: "middle",
                    },
                },
            ];

            const lastReceivedColumn = {
                id: "last_received_date",
                header: (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        Last Received Date
                        <Tooltip arrow title="The date inventory was received, either from a supplier or another store/DC.">
                            <IconButton size="small">
                                <InfoOutlinedIcon fontSize="small" sx={{ color: "#0A4FB4" }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                ),
                accessorKey: "last_received_date",
                cell: (row) => {
                    const val = row?.getValue();
                    const displayDateText = val ? moment(val, 'YYYY-MM-DD').format("MMM DD, YYYY") : "-";
                    const humanMoment = val ? moment(val, 'YYYY-MM-DD').fromNow() : "Never";
                    return <Box sx={{
                        width: '7em', marginX: 'auto', display: "flex", gap: .5,
                        justifyContent: !!val ? "flex-start" : "center"
                    }} title={humanMoment}>
                        <Typography>
                            {displayDateText}
                        </Typography>
                    </Box>
                },
                customStyle: {
                    width: "15rem",
                    textAlign: "left",
                    verticalAlign: "middle",
                },
            };

            if (hasLastReceivedDate) {
                columns.splice(4, 0, lastReceivedColumn);
            }

            columns.push(
                {
                    id: "rentables_articles",
                    header: "is Rentable?",
                    accessorKey: "rentables_articles",
                    cell: (row) => {
                        const value = row?.getValue();
                        return (
                            <Box sx={{ marginX: 'auto' }}>
                                <Typography>{value ? "Yes" : "No"}</Typography>
                            </Box>
                        );
                    },
                    customStyle: {
                        width: "8rem",
                        textAlign: "center",
                        verticalAlign: "middle",
                    },
                },
                {
                    id: "days_supply",
                    header: "Days Supply",
                    accessorFn: calcDaysSupply,
                    cell: (row) => {
                        const value = row.getValue();
                        let displayValue = "-";
                        if (value !== null && value !== undefined) {
                            const num = Number(value);
                            if (Math.abs(num) < 1 && num !== 0) {
                                // For values between -1 and 1 (excluding 0), round away from zero
                                displayValue = Math.sign(num) * 1;
                            } else {
                                // For all other numbers, use normal rounding
                                displayValue = Math.round(num);
                            }
                        }
                        return (
                            <Box sx={{ width: { sm: '30%' }, marginX: 'auto' }}>
                                <Typography textAlign={'center'}>
                                    {displayValue}
                                </Typography>
                            </Box>
                        );
                    },
                    customStyle: {
                        width: "10rem",
                        textAlign: "left",
                        verticalAlign: "middle",
                    },
                },
                {
                    id: "csl",
                    header: "CSL",
                    accessorKey: "csl",
                    cell: (row) => {
                        const value = row?.getValue();
                        let displayValue = "-";
                        if (value !== undefined && value !== null) {
                            const num = Number(value);
                            displayValue = Number.isInteger(num) ? num : num.toFixed(0);
                        }
                        return (
                            <Box sx={{ marginX: 'auto' }}>
                                <Typography>
                                    {displayValue}%
                                </Typography>
                            </Box>
                        );
                    },
                    customStyle: {
                        width: "6rem",
                        textAlign: "center",
                        verticalAlign: "middle",
                    },
                },
                {
                    id: "callout",
                    header: "CallOut",
                    cell: (row) => {
                        const inventoryValue = row?.row?.original?.inventory_value
                        const salesAtCostCount = row?.row?.original?.sales_at_cost_transaction_count
                        const rollingSalesAtCost = row?.row?.original?.rolling_week_sales_at_cost
                        const displayTypeValue = row?.row?.original?.display_type_value

                        let resultDaysSupply = null;
                        // Prevent division by zero or undefined
                        if (rollingSalesAtCost && rollingSalesAtCost !== 0 && rollingSalesAtCost !== '0') {
                            const adjustedInventory = inventoryValue - (inventoryValue * (displayTypeValue / 100));
                            const finalizedRollingSalesAtCost = (rollingSalesAtCost / salesAtCostCount);
                            resultDaysSupply = adjustedInventory / finalizedRollingSalesAtCost;
                        }
                        const cslValue = row?.row?.original?.csl;
                        const rentable = row?.row?.original?.rentables_articles;
                        const noCallOuts = !rentable && !((cslValue >= 75 && resultDaysSupply <= 15) || (cslValue >= 75 || resultDaysSupply >= 45) || (cslValue <= 70 && resultDaysSupply <= 15));

                        let icon = null;
                        if (rentable === true || noCallOuts) {
                            icon = null;
                        } else if (cslValue >= 75 && resultDaysSupply <= 15) { //Check Order Settings
                            icon = <Tooltip title='Order Settings' placement="left"><InventoryTwoToneIcon fontSize="large" sx={{ color: "#002060" }} /></Tooltip>;
                        } else if (cslValue >= 75 || resultDaysSupply >= 45) { //Check Store Display/Handing
                            icon = <Tooltip title='Store Display' placement="left"><StorefrontTwoToneIcon fontSize="large" sx={{ color: "#EA324C" }} /></Tooltip>;
                        } else if (cslValue <= 70 && resultDaysSupply <= 15) { //Check for Stock Problem
                            icon = <Tooltip title='Stock Problem' placement="left"><Inventory2TwoToneIcon fontSize="large" sx={{ color: "#F2AC42" }} /></Tooltip>;
                        }

                        return (
                            <Box sx={{ width: { sm: '20%' }, marginX: 'auto' }} >
                                {icon}
                            </Box >
                        );
                    },
                    customStyle: {
                        width: "13rem",
                        textAlign: "left",
                        verticalAlign: "middle",
                    },
                }
            );

            return columns;
        },
        [hasLastReceivedDate]
    );

    if (isLoadingZeroSalesData) {
        return <LoadingComponent />;
    }

    if (!filters?.store_id && !overviewTableIsEmpty) {
        return (
            <Box flexGrow={1} xs={12} sx={{ backgroundColor: "#ffffff", borderRadius: "0 0 .5rem .5rem" }} >
                <Grid container justifyContent="center" alignItems="center" py={6} borderRadius={0}>
                    <Typography
                        sx={{ fontSize: "1.5rem", color: "black", textAlign: "center" }}
                    >
                        Please select a store to view the article(s)
                    </Typography>
                </Grid>
            </Box>
        );
    }

    if (!zeroSalesData || zeroSalesData?.length === 0 || overviewTableIsEmpty || (!filters?.store_id && overviewTableIsEmpty)) {
        return (
            <Box flexGrow={1} xs={12} sx={{ backgroundColor: "#ffffff", borderRadius: "0 0 .5rem .5rem" }} >
                <Grid container justifyContent="center" alignItems="center" py={6} borderRadius={0}>
                    <Typography
                        sx={{ fontSize: "1.5rem", color: "black", textAlign: "center" }}
                    >
                        There’s no available data.
                    </Typography>
                </Grid>
            </Box>
        );
    }

    return (
        <Grid>
            <Grid item xs={12} sm={12} display={'flex'} justifyContent={'space-evenly'} sx={{ boxShadow: '0px 4px 3px rgba(0, 0, 0, 0.15)', border: '1px solid #e0e0e0', backgroundColor: '#ffffff' }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                        size="medium"
                        checked={checkedActions.storeDisplay}
                        onChange={handleCheckboxChange('storeDisplay')}
                    />
                    <StorefrontTwoToneIcon fontSize="medium" sx={{ color: "#EA324C" }} />
                    <Typography variant="body2" color={'black'}>
                        Store Display: <b>{actionCounts.storeDisplay}</b>
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                        size="medium"
                        checked={checkedActions.stockProblem}
                        onChange={handleCheckboxChange('stockProblem')}
                    />
                    <Inventory2TwoToneIcon fontSize="small" sx={{ color: "#F2AC42" }} />
                    <Typography variant="body2" color={'black'} >
                        Stock Problem: <b>{actionCounts.stockProblem}</b>
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                        size="medium"
                        checked={checkedActions.orderSettings}
                        onChange={handleCheckboxChange('orderSettings')}
                    />
                    <InventoryTwoToneIcon fontSize="small" sx={{ color: "#002060" }} />
                    <Typography variant="body2" color={'black'} >
                        Order Settings: <b>{actionCounts.orderSettings}</b>
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                        size="medium"
                        checked={checkedActions.rentableValue}
                        onChange={handleCheckboxChange('rentableValue')}
                    />
                    <Typography variant="body2" color={'black'}>
                        Rentable Articles: <b>{actionCounts.rentableValue}</b>
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                        size="medium"
                        checked={checkedActions.noCallOuts}
                        onChange={handleCheckboxChange('noCallOuts')}
                    />
                    <Typography variant="body2" color={'black'}>
                        No CallOut:  <b>{actionCounts.noCallOuts}</b>
                    </Typography>
                </Box>
            </Grid>
            <Item>
                <Grid container xs={12}>
                    <TableComponentV2
                        columns={storeZeroSalesColumns}
                        data={filteredZeroSalesData || []}
                        tableId="storeZeroSalesTable"
                        isCompact
                        customTableHeaderStyle={{ backgroundColor: "#caddeb" }}
                        customTableContainerStyle={{ borderRadius: 0, boxShadow: "none" }}
                        hasPagination
                    />
                </Grid>
            </Item>
        </Grid>
    );
}
